<template>
  <v-alert v-if="!purposeOnly" type="warning" density="compact" class="mb-4">
    {{ $t("ncrm.integrations.mailjet.privacy.mappingHint") }}
  </v-alert>
  <!-- Proprietarul canonic al scopurilor este workspace-ul Protecția datelor. -->
  <div v-if="purposeLink" class="mb-4"><NcrmPrivacyPurposeLink /></div>
  <v-alert v-if="feedback.message" :type="feedback.type" closable class="mb-4"
    @click:close="clearFeedback">
    {{ feedback.message }}
  </v-alert>
  <v-alert v-if="loadError" type="error" class="mb-4">
    {{ loadError }}
    <template #append>
      <v-btn variant="text" @click="fetchTemplates">{{ $t("ncrm.integrations.mailjet.privacy.retry") }}</v-btn>
    </template>
  </v-alert>

  <div v-if="draftPurposes.length" class="mb-4">
      <div class="text-subtitle-2 mb-1">{{ $t("ncrm.integrations.mailjet.privacy.activationTitle") }}</div>
      <div class="text-caption text-medium-emphasis mb-2">
        {{ $t("ncrm.integrations.mailjet.privacy.activationHint") }}
      </div>
      <v-btn v-for="purpose in draftPurposes" :key="purpose.id" size="small" variant="outlined"
        color="primary" class="me-2 mb-2" :loading="activatingId === purpose.id"
        :disabled="activatingId !== null" @click="pendingPurpose = purpose">
        {{ $t("ncrm.integrations.mailjet.privacy.activate", { name: purpose.name }) }}
      </v-btn>
  </div>

  <v-dialog :model-value="Boolean(pendingPurpose)" max-width="520" persistent>
      <v-card>
        <v-card-title>{{ $t("ncrm.integrations.mailjet.privacy.activationTitle") }}</v-card-title>
        <v-card-text>{{ $t("ncrm.integrations.mailjet.privacy.activationConfirm", { name: pendingPurpose?.name }) }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="pendingPurpose = null">{{ $t("ncrm.integrations.mailjet.privacy.cancel") }}</v-btn>
          <v-btn color="primary" variant="flat" :loading="activatingId !== null" @click="activatePendingPurpose">
            {{ $t("ncrm.integrations.mailjet.privacy.confirmActivation") }}
          </v-btn>
        </v-card-actions>
      </v-card>
  </v-dialog>

  <v-data-table-server v-if="!purposeOnly"
    :headers="headers"
    :items="templates"
    :items-length="total"
    :loading="loading"
    v-model:items-per-page="perPage"
    v-model:page="page"
    :items-per-page-options="[25, 50, 100]"
    :no-data-text="$t('ncrm.integrations.mailjet.privacy.noTemplates')"
    density="comfortable"
    class="elevation-0"
    item-key="id"
  >
    <template #item.privacy_purpose_id="{ item }">
      <div class="d-flex align-center ga-2 py-2">
        <v-select v-model="mappingDraft[item.id]" :items="purposeItems" density="compact"
          hide-details class="ncrm-mailjet-privacy__purpose" />
        <v-btn size="small" color="primary" variant="tonal" :loading="savingId === item.id"
          :disabled="!mappingChanged(item) || savingId !== null" @click="saveMapping(item)">
          {{ $t("ncrm.integrations.mailjet.privacy.save") }}
        </v-btn>
      </div>
    </template>
    <template #item.last_synced_at="{ item }">{{ formatDate(item.last_synced_at) }}</template>
    <template v-if="!privacyOnly" #item.show_in_crm_ui="{ item }">
      <v-switch
        :model-value="Boolean(item.show_in_crm_ui)"
        color="primary"
        density="compact"
        hide-details
        inset
        :disabled="togglingId === item.id"
        @update:model-value="toggleVisibility(item, $event)"
      />
    </template>
  </v-data-table-server>
</template>

<script>
import axios from "axios";
import NcrmPrivacyPurposeLink from "@/components/ncrm/privacy/NcrmPrivacyPurposeLink.vue";

export default {
  name: "NcrmMailjetTemplatesTab",
  components: { NcrmPrivacyPurposeLink },
  props: {
    accountId: { type: [Number, String], default: null },
    privacyOnly: { type: Boolean, default: false },
    purposeOnly: { type: Boolean, default: false },
    // Linkul `Gestionează scopurile` navighează în altă pagină. O suprafață cu
    // date nesalvate (dialogul de trimitere email) îl oprește, ca să nu le piardă.
    purposeLink: { type: Boolean, default: true },
  },
  emits: ["notify", "purpose-activated"],
  data() {
    return {
      templates: [],
      purposes: [],
      mappingDraft: {},
      total: 0,
      loading: false,
      loadError: "",
      feedback: { type: "success", message: "" },
      togglingId: null,
      savingId: null,
      activatingId: null,
      pendingPurpose: null,
      page: 1,
      perPage: 50,
    };
  },
  computed: {
    headers() {
      const items = [
        { title: this.$t("ncrm.integrations.mailjet.panel.headers.name"), key: "name" },
        { title: this.$t("ncrm.integrations.mailjet.privacy.providerPurpose"), key: "purpose" },
        { title: this.$t("ncrm.integrations.mailjet.privacy.dataPurpose"), key: "privacy_purpose_id", sortable: false },
        { title: this.$t("ncrm.integrations.mailjet.panel.headers.lastSynced"), key: "last_synced_at" },
      ];
      if (!this.privacyOnly) items.push({
        title: this.$t("ncrm.integrations.mailjet.panel.headers.showInCrmUi"),
        key: "show_in_crm_ui", sortable: false, align: "center",
      });
      return items;
    },
    draftPurposes() { return this.purposes.filter(({ status }) => status === "draft"); },
    purposeItems() {
      return [
        { title: this.$t("ncrm.integrations.mailjet.privacy.noPurpose"), value: null },
        ...this.purposes.map((purpose) => ({
          title: `${purpose.name} · ${this.$t(`ncrm.integrations.mailjet.privacy.status.${purpose.status}`)}`,
          value: purpose.id,
          props: { disabled: purpose.status !== "active" },
        })),
      ];
    },
  },
  watch: {
    accountId: {
      handler() { const changed = this.page !== 1; this.page = 1; if (!changed) this.fetchTemplates(); },
      immediate: true,
    },
    page() { this.fetchTemplates(); },
    perPage() { const changed = this.page !== 1; this.page = 1; if (!changed) this.fetchTemplates(); },
  },
  methods: {
    clearFeedback() { this.feedback = { type: "success", message: "" }; },
    formatDate(value) {
      const date = value ? new Date(value) : null;
      if (!date || Number.isNaN(date.getTime())) return "—";
      const pad = (part) => String(part).padStart(2, "0");
      return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
    extractError(error, fallbackKey) {
      return error?.response?.data?.error || error?.message || this.$t(fallbackKey);
    },
    mappingChanged(item) { return (this.mappingDraft[item.id] ?? null) !== (item.privacy_purpose_id ?? null); },
    async fetchTemplates() {
      if (this.purposeOnly) {
        this.loading = true; this.loadError = "";
        try {
          const { data } = await axios.get("/ncrm/privacy/purposes", { params: { limit: 100 } });
          this.purposes = (data?.items || []).filter(({ channels }) => channels?.includes("email"));
        } catch (error) { this.loadError = this.extractError(error, "ncrm.integrations.mailjet.privacy.loadError");
        } finally { this.loading = false; }
        return;
      }
      if (!this.accountId) return;
      this.loading = true;
      this.loadError = "";
      try {
        const params = { account_id: this.accountId, limit: this.perPage, offset: (this.page - 1) * this.perPage };
        const { data } = await axios.get("/ncrm/privacy/configuration/email", { params });
        this.templates = Array.isArray(data?.templates) ? data.templates : [];
        this.purposes = Array.isArray(data?.purposes) ? data.purposes : [];
        this.mappingDraft = Object.fromEntries(this.templates.map((item) => [item.id, item.privacy_purpose_id ?? null]));
        this.total = Number(data?.total || 0);
      } catch (error) {
        this.loadError = this.extractError(error, "ncrm.integrations.mailjet.privacy.loadError");
      } finally { this.loading = false; }
    },
    async activatePurpose(purpose) {
      this.activatingId = purpose.id;
      this.clearFeedback();
      try {
        const { data } = await axios.post(`/ncrm/privacy/configuration/email-purposes/${purpose.id}/activate`);
        this.$emit("purpose-activated", purpose.id);
        await this.fetchTemplates();
        this.feedback = { type: "success", message: this.$t("ncrm.integrations.mailjet.privacy.activationSuccess", { count: data?.projections_updated || 0 }) };
      } catch (error) {
        this.feedback = { type: "error", message: this.extractError(error, "ncrm.integrations.mailjet.privacy.activationError") };
      } finally { this.activatingId = null; }
    },
    async activatePendingPurpose() {
      if (!this.pendingPurpose) return;
      const purpose = this.pendingPurpose;
      await this.activatePurpose(purpose);
      if (this.feedback.type === "success") this.pendingPurpose = null;
    },
    async saveMapping(item) {
      this.savingId = item.id;
      this.clearFeedback();
      try {
        const purposeId = this.mappingDraft[item.id] ?? null;
        await axios.put(`/ncrm/privacy/configuration/mailjet-templates/${item.id}`, {
          account_id: this.accountId, purpose_id: purposeId,
        });
        item.privacy_purpose_id = purposeId;
        this.feedback = { type: "success", message: this.$t("ncrm.integrations.mailjet.privacy.mappingSaved", { name: item.name }) };
      } catch (error) {
        this.feedback = { type: "error", message: this.extractError(error, "ncrm.integrations.mailjet.privacy.mappingError") };
      } finally { this.savingId = null; }
    },
    async toggleVisibility(item, value) {
      if (!this.accountId || !item?.id) return;
      this.togglingId = item.id;
      try {
        await axios.patch(`/ncrm/integrations/mailjet/accounts/${this.accountId}/templates/${item.id}`, { show_in_crm_ui: Boolean(value) });
        const idx = this.templates.findIndex((row) => row.id === item.id);
        if (idx >= 0) this.templates.splice(idx, 1, { ...item, show_in_crm_ui: value ? 1 : 0 });
        this.$emit("notify", {
          color: "success",
          message: this.$t(
            value
              ? "ncrm.integrations.mailjet.panel.alerts.templateVisibilityOn"
              : "ncrm.integrations.mailjet.panel.alerts.templateVisibilityOff",
            { name: item.name }
          ),
        });
      } catch (error) {
        this.$emit("notify", { color: "error", message: this.extractError(error, "ncrm.integrations.mailjet.panel.errors.templateVisibility") });
      } finally {
        this.togglingId = null;
      }
    },
  },
};
</script>

<style scoped>
.ncrm-mailjet-privacy__purpose { min-width: 220px; }
@media (max-width: 600px) { .ncrm-mailjet-privacy__purpose { min-width: 180px; } }
</style>
